import { Container } from '@cleartrip/ct-design-components'
import React, { RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react'

import BenefitList from '@/components/srp/FareCards/BenefitList'
import BrandedBenefitInfo from '@/components/srp/FareCards/FareBlock/FareBenefits/BrandedBenefitInfo'
import FareCard from '@/components/srp/FareCards/FareBlock/FareCard'
import FareCardLoader from '@/components/srp/FareCards/FareBlock/FareCardLoader'
import { getFareCabin } from '@/components/srp/FareCards/FareFamilyModal/crossCabinUtils'
import PEExperienceMediaCard from '@/components/srp/FareCards/FareFamilyModal/PEExperienceMediaCard'
import PEUpgradeCalloutCard from '@/components/srp/FareCards/FareFamilyModal/PEUpgradeCalloutCard'
import Toast from '@/components/Toast'
import { CabinTabId, FareCabinTabId } from '@/constants/flights/cabinClass'
import {
    CabinTabKey,
    CROSS_CABIN_SAME_TYPE_MESSAGE,
    FARE_CARD_GAP_PX,
    FARE_CARD_STRIDE_PX,
    PE_EXPERIENCE_CARD_INSERT_INDEX,
    PE_EXPERIENCE_CARD_WIDTH_PX,
    UpgradeNudgeVariant,
} from '@/constants/flights/premiumEconomy'
import { BRANDED_FARE_HEADER_PREFIX, BRANDED_FARE_RETURN_TRIP_DESCRIPTION, DISABLED_FARE_TOAST_MSG_BRANDED_FARE } from '@/constants/Srp'
import { FareType, ServicesData } from '@/types/srp'
import { isEmpty } from '@/utils/general/browserHelper'
import { hasAdditionalBenefitsWorth, isCTBrandedFareCardCheck, isCTBrandedFareCardSelectedOnward } from '@/utils/srp'
import { getCouponDisplayAndDiscountValue, getFarePricingAttributes } from '@/utils/srp/helper'
import { isAnyFareVisaRejectionEligible } from '@/utils/srp/visaRejection'

import FareHorizontalScrollStrip from '../FareHorizontalScrollStrip'

type FareFamilyBlockProps = {
    fares: FareType[]
    showPerAdultPricing: boolean
    selectedFare: FareType
    selectFare: (fare: FareType, index: number) => void
    servicesData: ServicesData
    isServicesLoading: boolean
    retryCountForServices: number
    onRetryForBenefits: () => void
    isRT: any
    couponData: any
    cardKey: any
    faresObj: any
    selectedFaresObj: any
    selectedTabIndex: any
    isFFFlow?: boolean
    interstitialFaresLoading?: boolean
    totalFareCount?: number
    isSpecialRT: boolean
    /** RT only — fareId currently selected on the OPPOSITE tab. Forwarded to
     *  FareCard so it can build the combined `[onward, return]` WOW key. */
    otherTabSelectedFareId?: string
    isIntl?: boolean
    onOpenVdcEducation?: () => void
    cardDetails?: any[]
    subTravelOptions?: Record<string, any>
    showPeUpgradeCallout?: boolean
    onPeUpgrade?: () => void
    selectedCabin?: CabinTabId
    /** Theme cabin for card chrome — Up Front keeps ECONOMY look. */
    themeCabin?: FareCabinTabId
    /** BE: `cabinClassSummary[selectedCabin].cabinPromoBanner.title` for the promo tile. */
    cabinPromoBannerTitle?: string
    isEconomySearchFlow?: boolean
    /** BE-driven upgrade amount (fares[ecoFareId].upgradeCallout.amount) — undefined falls back to the placeholder. */
    peUpgradeAmount?: number
    peUpgradeTitle?: string | null
    peUpgradeImageSrc?: string | null
    peUpgradeVideoSrc?: string | null
    peUpgradeVariant?: UpgradeNudgeVariant
    /**
     * PE test-variant flag (§2d loading-screen fix). The pre-existing skeleton-card
     * gate below only fired for `isFFFlow` (non-CFW) — CFW flow (isFFFlow=false)
     * never showed FareCardLoader skeletons at all, so the PE interstitial (which
     * always fetches on CFW) rendered no cards, then popped all of them in at once
     * once the call resolved. This extends the gate to also cover the PE test
     * variant, without touching the existing `isFFFlow` control-path behaviour.
     */
    showPeInterstitialLayout?: boolean
    /**
     * Parent already filtered `fares` to the active cabin tab (BE cabinFareIds /
     * special-RT cabin filter). Forwarded so FareCard does not re-lock Select via
     * resolveDisplayCabin vs selectedCabin.
     */
    isCabinFilteredList?: boolean
    /** RT return tab: theme cabin chosen on the onward leg (for cross-cabin guard). */
    onwardLegCabin?: FareCabinTabId | CabinTabKey
    showVideoModal: () => void
    /** BE cabin media image URLs (videos filtered out) for the PE experience carousel. */
    peCarouselImages?: string[]
}
const FareFamilyBlock: React.FC<FareFamilyBlockProps> = ({
    fares,
    showPerAdultPricing,
    selectedFare,
    selectFare,
    servicesData,
    isServicesLoading,
    retryCountForServices,
    onRetryForBenefits,
    isRT,
    couponData,
    cardKey,
    faresObj,
    selectedFaresObj,
    selectedTabIndex,
    isFFFlow,
    interstitialFaresLoading,
    totalFareCount,
    isSpecialRT,
    isIntl,
    otherTabSelectedFareId,
    onOpenVdcEducation,
    cardDetails = [],
    subTravelOptions = {},
    showPeUpgradeCallout = false,
    onPeUpgrade,
    selectedCabin,
    themeCabin,
    cabinPromoBannerTitle,
    isEconomySearchFlow = false,
    peUpgradeAmount,
    peUpgradeTitle,
    peUpgradeImageSrc,
    peUpgradeVideoSrc,
    peUpgradeVariant,
    showPeInterstitialLayout = false,
    isCabinFilteredList = false,
    onwardLegCabin,
    showVideoModal,
    peCarouselImages,
}) => {
    const benefitRef: RefObject<HTMLDivElement> = useRef(null)
    const peAttachedCardRef = useRef<HTMLDivElement>(null)
    const fareStripMeasureRef = useRef<HTMLDivElement>(null)
    const [peAttachedCardHeight, setPeAttachedCardHeight] = useState<number | undefined>()
    const [equalizeMinHeightPx, setEqualizeMinHeightPx] = useState<number | undefined>()
    const [disabledFareToastMsg, setDisabledFareToastMsg] = useState('')
    const disabledFareToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const isCTBrandedFareCardSelectedOW = isCTBrandedFareCardSelectedOnward(selectedFaresObj)
    // Economy search flow only; hidden for PE/Business/First searches. Declared up
    // here because the card-height measurement effect below depends on it.
    const showPeExperienceCard = isEconomySearchFlow && selectedCabin === 'PREMIUM_ECONOMY'
    // Card chrome follows theme cabin (Up Front → ECONOMY), not the display tab id.
    const cabinForTheme = (themeCabin || selectedCabin) as CabinTabKey | undefined

    const showDisabledFareToast = (fare: FareType) => {
        const onwardSelectedCabin =
            !!selectedTabIndex && !isSpecialRT && !isEmpty(selectedFaresObj?.[0])
                ? (isCabinFilteredList && onwardLegCabin
                    ? onwardLegCabin
                    : getFareCabin(selectedFaresObj[0]))
                : undefined
        const fareCabin = getFareCabin(fare)
        const fareCabinForGuard =
            isCabinFilteredList && cabinForTheme ? cabinForTheme : fareCabin
        const isCabinMismatch =
            !!fareCabinForGuard &&
            ((!!onwardSelectedCabin && onwardSelectedCabin !== fareCabinForGuard) ||
                (!isCabinFilteredList && !!cabinForTheme && cabinForTheme !== fareCabin))
        if (showPeInterstitialLayout && isCabinMismatch) {
            setDisabledFareToastMsg(CROSS_CABIN_SAME_TYPE_MESSAGE)
            return
        }
        const isBrandedFare = isCTBrandedFareCardCheck(fare)
        if (isCTBrandedFareCardSelectedOW && !isBrandedFare) {
            setDisabledFareToastMsg(
                DISABLED_FARE_TOAST_MSG_BRANDED_FARE.ONWARD
            )
        } else if (!isCTBrandedFareCardSelectedOW && isBrandedFare) {
            setDisabledFareToastMsg(DISABLED_FARE_TOAST_MSG_BRANDED_FARE.RETURN)
        }
    }

    useLayoutEffect(() => {
        if (!benefitRef.current) return
        benefitRef.current.style.marginTop = '0px'
        // Clear Value Max spacer before paint when the visible strip has no branded
        // card — otherwise leftover marginBottom stretches the fare column and
        // loaders flash tall then shrink (BenifitSummary re-applies when one mounts).
        const hasValueMaxSummary = fares?.some(
            (fare) => isCTBrandedFareCardCheck(fare) && !isEmpty(fare?.farePerks)
        )
        if (!hasValueMaxSummary) {
            benefitRef.current.style.marginBottom = '0px'
        }
    }, [selectedTabIndex, selectedCabin, fares])

    // Keep the PE upgrade callout and the PE promo tile locked to the attached fare
    // card so a taller sibling (e.g. Value Max + insurance) cannot stretch them.
    useLayoutEffect(() => {
        if (!showPeUpgradeCallout && !showPeExperienceCard) {
            return
        }
        const el = peAttachedCardRef.current
        if (!el) return

        const updateHeight = () => {
            const nextHeight = Math.round(el.getBoundingClientRect().height)
            setPeAttachedCardHeight(nextHeight > 0 ? nextHeight : undefined)
        }
        updateHeight()

        if (typeof ResizeObserver === 'undefined') return
        const resizeObserver = new ResizeObserver(updateHeight)
        resizeObserver.observe(el)
        return () => resizeObserver.disconnect()
    }, [
        showPeUpgradeCallout,
        showPeExperienceCard,
        fares,
        selectedFare,
        isServicesLoading,
        interstitialFaresLoading,
    ])

    // Equalize each benefit row's height across the legend and all fare cards so the legends
    // stay aligned no matter how many lines a value wraps to.
    const rowSyncRef = useRef<HTMLDivElement>(null)
    useLayoutEffect(() => {
        const root = rowSyncRef.current
        if (!root || typeof window === 'undefined') {
            return
        }
        const equalizeRows = () => {
            const rows = Array.from(root.querySelectorAll<HTMLElement>('[class*="ff-row-"]'))
            const groups: Record<string, HTMLElement[]> = {}
            rows.forEach(el => {
                const key = (el.className.match(/ff-row-([A-Z_]+)/) || [])[1]
                if (!key) {
                    return
                }
                el.style.minHeight = ''
                ;(groups[key] = groups[key] || []).push(el)
            })
            Object.values(groups).forEach(list => {
                const max = list.reduce((acc, el) => Math.max(acc, el.offsetHeight), 0)
                list.forEach(el => {
                    el.style.minHeight = `${max}px`
                })
            })
        }
        equalizeRows()
        const rafId = requestAnimationFrame(equalizeRows)
        window.addEventListener('resize', equalizeRows)
        return () => {
            cancelAnimationFrame(rafId)
            window.removeEventListener('resize', equalizeRows)
        }
    }, [fares, selectedFare, selectedTabIndex, isServicesLoading, servicesData, couponData, isSpecialRT])

    useEffect(() => {
        if (!disabledFareToastMsg) return
        if (disabledFareToastTimerRef.current) {
            clearTimeout(disabledFareToastTimerRef.current)
        }
        const TOAST_TIMER = 4000
        disabledFareToastTimerRef.current = setTimeout(() => setDisabledFareToastMsg(''), TOAST_TIMER)
        return () => {
            if (disabledFareToastTimerRef.current) {
                clearTimeout(disabledFareToastTimerRef.current)
            }
        }
    }, [disabledFareToastMsg])
    const faresWithParsedData = fares.map((fare) => {
        const { couponCallout, displayPrice, strikeOffPrice, smbVasCouponCallout: inlineSmbVasCallout } = getFarePricingAttributes({
            fare,
            showPerAdultPricing
        })

        // Special RT never satisfies the selectedTabIndex gate, but its own fare resolves the coupon alone.
        const pairCoupon =
            isSpecialRT || (selectedTabIndex === 1 && !isEmpty(selectedFaresObj[0]))
                ? getCouponDisplayAndDiscountValue({
                    selectedFares: isSpecialRT ? [fare] : [selectedFaresObj[0], fare],
                    showPerAdultPricing,
                    isRT,
                    couponData,
                    cardKey,
                    isSpecialRT
                })
                : null
        const couponDisplayValueForFare = pairCoupon?.couponDiscountDisplayValue || ''
        const rtSmbVasCallout = pairCoupon?.smbVasCouponCallout || ''
        // Only Special RT slashes here: a regular RT card prices one leg, so its NEP stays on the footer.
        // SplRT slasher: couponUIResponse.coupons[cardKey][fareKey].couponDetails.nepDetails.slasherCouponCode
        const nep = isSpecialRT ? pairCoupon?.nepDetails : null

        return {
            fare,
            couponDisplayValueForFare,
            couponCallout,
            smbVasCouponCallout: isSpecialRT
                ? (rtSmbVasCallout || inlineSmbVasCallout)
                : isRT
                    ? rtSmbVasCallout
                    : inlineSmbVasCallout,
            displayPrice: nep?.hasNepStrikeOff ? nep.displayPrice : displayPrice,
            strikeOffPrice: nep?.hasNepStrikeOff ? nep.strikeOffPrice : strikeOffPrice,
            nepSlasherCouponCode: nep?.slasherCouponCode || '',
        }
    })    
    const isSubtitlePresent = !isEmpty(fares?.find(fare => !isEmpty(fare?.displayText?.displaySubTitle)))
    const isCTBrandedFareCardPresent = fares?.some((fare: any) => isCTBrandedFareCardCheck(fare))
    const isBtmCalloutPresent = !isCTBrandedFareCardPresent && isFFFlow && !isEmpty(fares?.find(hasAdditionalBenefitsWorth))
    // Value Max BenifitSummary and/or "Additional benefits worth" make some cards
    // taller — stretch siblings and leave empty footer space on cards without one.
    const equalizeBottomBenefitsSpace = fares?.some((fare: any) => {
        if (isCTBrandedFareCardCheck(fare) && !isEmpty(fare?.farePerks)) return true
        if (isFFFlow && hasAdditionalBenefitsWorth(fare)) return true
        return false
    })

    // Measure natural card-shell heights once per strip change, then pin every
    // shell to that height. No ResizeObserver growth — observing forced-height
    // shells while they flex-grow inside a stretched track ratcheted forever
    // (clicking Value Max made cards extend without stopping).
    useLayoutEffect(() => {
        if (!equalizeBottomBenefitsSpace) {
            setEqualizeMinHeightPx(undefined)
            return
        }

        // Drop any previous forced height so the next frames measure content-native
        // sizes (avoids sticky tall heights after cabin/tab changes).
        setEqualizeMinHeightPx(undefined)

        let raf1 = 0
        let raf2 = 0
        let raf3 = 0
        const measure = () => {
            const root = fareStripMeasureRef.current
            if (!root) return
            let max = 0
            root.querySelectorAll('[data-fare-equalize-root]').forEach((node) => {
                max = Math.max(max, (node as HTMLElement).getBoundingClientRect().height)
            })
            if (max > 0) {
                setEqualizeMinHeightPx(Math.round(max))
            }
        }

        // Double rAF: wait until the undefined-height commit has laid out.
        // Third rAF catches BenifitSummary after it mounts and reserves label space.
        raf1 = requestAnimationFrame(() => {
            raf2 = requestAnimationFrame(() => {
                measure()
                raf3 = requestAnimationFrame(measure)
            })
        })

        return () => {
            cancelAnimationFrame(raf1)
            cancelAnimationFrame(raf2)
            cancelAnimationFrame(raf3)
        }
    }, [
        equalizeBottomBenefitsSpace,
        fares,
        selectedTabIndex,
        selectedCabin,
        isServicesLoading,
        interstitialFaresLoading,
    ])

    const isCouponPresentInAnyFare = faresWithParsedData.some(d => d.couponCallout || d.couponDisplayValueForFare)

    const interstitialSkeletonCount = Math.max(0, (totalFareCount ?? 0) - fares.length)

    // PE tile is pinned at the 4th column (or the last slot if fewer than 3 cards) while cards scroll under it.
    const peSlotIndex = Math.min(faresWithParsedData.length, PE_EXPERIENCE_CARD_INSERT_INDEX)
    const peOverlayLeftPx = FARE_CARD_GAP_PX + peSlotIndex * FARE_CARD_STRIDE_PX
    const peTileOverlapsCard = faresWithParsedData.length > PE_EXPERIENCE_CARD_INSERT_INDEX

    const isCfw = new URLSearchParams(window.location.search).get("isCfw") === 'true'
    
    const isVisaAvailable = isAnyFareVisaRejectionEligible(fares, {
        isSpecialRT,
        cardDetails,
        subTravelOptions,
    })
    
    const interstitialTopInset = 20
    return (
        <Container ref={rowSyncRef} className="flex  pb-5"
            data-testid="fare-strip"
            data-fare-count={faresWithParsedData.length}
            data-skeleton-count={interstitialFaresLoading ? interstitialSkeletonCount : 0}
            style={{
            paddingLeft: '24px',
            paddingRight: '12px',
        }}>
            <Container ref={benefitRef} className="flex flex-column flex-end" css={{paddingTop: `${interstitialTopInset}px`, paddingBottom: isBtmCalloutPresent ? "44px": "10px", minWidth: '164px'}}>
                {selectedTabIndex && isCTBrandedFareCardSelectedOW ? <BrandedBenefitInfo
                    brandedName={selectedFaresObj[0]?.displayText?.displayTitle || ''}
                    description={BRANDED_FARE_RETURN_TRIP_DESCRIPTION}
                    headerPrefix={BRANDED_FARE_HEADER_PREFIX}
                /> : null}
                <BenefitList isIntl={isIntl} isVisaAvailable={isVisaAvailable} />
            </Container>       
            <Container
                ref={fareStripMeasureRef}
                className="p-relative flex flex-column flex-1"
                css={{ minWidth: 0 }}
            >
            <FareHorizontalScrollStrip
                scrollResetKey={selectedTabIndex}
                topInsetPx={interstitialTopInset}
            >
                {faresWithParsedData.map((data, index) => {
                    const {
                        fare, 
                        couponDisplayValueForFare, 
                        displayPrice, 
                        strikeOffPrice, 
                        couponCallout,
                        smbVasCouponCallout,
                        nepSlasherCouponCode,
                    } = data
                    const {
                        fareCategory,
                        fareId,
                        displayText,
                        travelOptionId,
                        additionalBenefitWorth,
                        bestValue,
                        ptcFare = false
                    } = fare
                    const { displaySubTitle, displayTitle } = displayText || {}
                    const isPremiumCard = isCfw ? fareCategory === 'CORPORATE' : !!bestValue 
                    const showBadge = isCfw ? fareCategory === 'CORPORATE' : !!bestValue 
                    const ptcTag = ptcFare ? displayTitle : ''
                    const isSelected = !isEmpty(selectedFare) && fareId === selectedFare.fareId
                    const showLeftTooltip = index >= 2

                    const isFirstCardWithPeUpgrade = index === 0 && showPeUpgradeCallout

                    const fareCard = (
                        <FareCard
                            additionalBenefitsWorth={additionalBenefitWorth}
                            benefitRef={benefitRef}
                            cardDetails={cardDetails}
                            // No `|| couponCallout` fallback: BE leaves fare-level coupon details empty for RT.
                            couponCallout={isRT ? couponDisplayValueForFare : couponCallout}
                            displayPrice={displayPrice}
                            displaySubTitle={displaySubTitle}
                            displayTitle={displayTitle}
                            equalizeBottomBenefitsSpace={equalizeBottomBenefitsSpace}
                            equalizeMinHeightPx={equalizeMinHeightPx}
                            fare={fare}
                            fareId={fareId}
                            isCabinFilteredList={isCabinFilteredList}
                            isCfw={isCfw}
                            isCouponPresentInAnyFare={isCouponPresentInAnyFare}
                            isCrossCabinGuardEnabled={showPeInterstitialLayout}
                            isFFFlow={isFFFlow}
                            isIntl={isIntl}
                            isPeDiscoveryActive={showPeInterstitialLayout}
                            isPeUpgradeAttached={isFirstCardWithPeUpgrade}
                            isPremiumCard={isPremiumCard}
                            isRT={!!isRT}
                            isSecond={index === 1 ? true : false}
                            isSelected={isSelected}
                            isServicesLoading={isServicesLoading}
                            isSpecialRT={isSpecialRT}
                            isSubtitlePresent={isSubtitlePresent}
                            onwardLegCabin={onwardLegCabin}
                            otherTabSelectedFareId={otherTabSelectedFareId}
                            ptcTag={ptcTag}
                            retryCountForServices={retryCountForServices}
                            rtTabIndex={selectedTabIndex}
                            selectedCabin={cabinForTheme}
                            selectedFaresObj={selectedFaresObj}
                            selectedTabIndex={selectedTabIndex}
                            selectFare={() => selectFare(fare, index)}
                            servicesData={servicesData}
                            showBadge={showBadge}
                            showLeftTooltip={showLeftTooltip}
                            showPerAdultPricing={showPerAdultPricing}
                            smbVasCouponCallout={smbVasCouponCallout || ''}
                            slasherCouponCode={nepSlasherCouponCode}
                            strikeOffPrice={strikeOffPrice}
                            subTravelOptions={subTravelOptions}
                            travelOptionId={travelOptionId}
                            onDisabledFareClick={() => showDisabledFareToast(fare)}
                            onOpenVdcEducation={onOpenVdcEducation}
                            onRetryForBenefits={onRetryForBenefits}
                        />
                    )

                    if (index === 0) {
                        return (
                            <Container
                                key={fareId}
                                className="flex flex-nowrap flex-shrink-0 p-relative"
                                style={{ alignSelf: 'flex-start', alignItems: 'flex-start' }}
                            >
                                <div ref={peAttachedCardRef}>{fareCard}</div>
                                {onPeUpgrade && (
                                    <PEUpgradeCalloutCard
                                        heightPx={peAttachedCardHeight}
                                        imageSrc={peUpgradeImageSrc}
                                        isVisible={showPeUpgradeCallout}
                                        showVideoModal={showVideoModal}
                                        title={peUpgradeTitle}
                                        upgradeAmount={peUpgradeAmount}
                                        variant={peUpgradeVariant}
                                        videoSrc={peUpgradeVideoSrc || undefined}
                                        onUpgrade={onPeUpgrade}
                                    />
                                )}
                            </Container>
                        )
                    }

                    return (
                        <React.Fragment key={fareId}>
                            {fareCard}
                        </React.Fragment>
                    )
                })}

                {
                    ((isFFFlow || showPeInterstitialLayout) && interstitialFaresLoading) ? Array(interstitialSkeletonCount).fill(0).map(() => <FareCardLoader/>) : null
                }

                {/* spacer so the card under the tile can scroll clear */}
                {showPeExperienceCard && peTileOverlapsCard && (
                    <Container aria-hidden css={{ width: `${FARE_CARD_STRIDE_PX}px`, flexShrink: 0 }} />
                )}
            </FareHorizontalScrollStrip>
            {showPeExperienceCard && (
                <Container
                    className="p-absolute z-1"
                    css={{
                        top: `${interstitialTopInset}px`,
                        bottom: 0,
                        // This column stretches to the taller of itself and the benefits
                        // column, so `bottom: 0` on its own lets the tile hang past the
                        // fare cards and into the footer; the measured card height pins
                        // it back to the card row (CTAIR-2597).
                        maxHeight: peAttachedCardHeight ? `${peAttachedCardHeight}px` : undefined,
                        // Pinned at its slot, but never past the column's right edge —
                        // the modal's reserved scrollbar gutter can leave the last slot
                        // a few px short, which cropped the tile.
                        left: `min(${peOverlayLeftPx}px, 100% - ${PE_EXPERIENCE_CARD_WIDTH_PX}px)`,
                        width: `${PE_EXPERIENCE_CARD_WIDTH_PX}px`,
                    }}
                >
                    <PEExperienceMediaCard  cabin={selectedCabin}
                    
                    resourceImages={peCarouselImages} showVideoModal={showVideoModal} title={cabinPromoBannerTitle} />
                </Container>
            )}
            </Container>
            <Toast
                bottomPos="200px"
                containerClass="br-12 py-3 px-4 mx-auto r-0 l-0 p-fixed"
                isVisible={!!disabledFareToastMsg}
                message={disabledFareToastMsg}
            />
        </Container>
    )
}

export default FareFamilyBlock
